import React, { useEffect, useState } from "react";

import {
  ThemeProvider,
  Typography,
  Grid,
  Autocomplete,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Card,
  CardHeader,
  CardContent,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Add as AddIcon } from "@mui/icons-material";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { styled } from "@mui/material/styles";
import {
  getCacheUsers,
  getUser,
  deleteUser,
} from "../../components/API/userAPI";
import EventIcon from "@mui/icons-material/Event";

import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
  components: {
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          "&.Mui-error": {
            color: "#f44336", // Custom error color
          },
        },
      },
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

function DeleteUsersComponent() {
  const [usersCache, setUsersCache] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  const [isFetchingUser, setIsFetchingUser] = useState(false); // Prevent double fetch for technician
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false,
  });

  useEffect(() => {
    const fetchCacheUsers = async () => {
      try {
        const usersCache = await getCacheUsers();
        setUsersCache(usersCache || []);
      } catch (err) {
        setError("Failed to fetch users cache");
      }
    };

    fetchCacheUsers();
  }, []);

  const handleSearchUser = (event, value) => {
    setIsFormVisible(false);
    setSelectedUser(value || null);
  };

  const handleSearchButtonClick = async () => {
    if (!selectedUser) {
      console.log("No user selected");
      return;
    }

    if (isFetchingUser) {
      console.log("Already fetching user details...");
      return;
    }

    setIsFetchingUser(true);
    setIsLoading(true);

    try {
      const fetchedUser = await getUser(selectedUser.id);
      setUser(fetchedUser);
      setIsFormVisible(true);
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setIsFetchingUser(false);
      setIsLoading(false);
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();

    if (window.confirm("Are you sure you want to delete this User?")) {
      setIsLoading(true);

      const { timestamp_create, timestamp_update, ...updatedUserParameters } =
        user;

      console.log("User to delete:", updatedUserParameters);
      console.log("User ID to delete:", user.id);

      try {
        const response = await deleteUser(user.id, updatedUserParameters);
        console.log("User delete response:", response);

        if (response) {
          setSuccessMessage("User deleted successfully!");

          setUsersCache((prevCache) =>
            prevCache.filter((userCache) => userCache.id !== user.id)
          );

          setSelectedUser("");
          setIsFormVisible(false);
        } else {
          setErrorMessage(
            "An unexpected error occurred while deleting the User. Please try again later."
          );
        }
      } catch (error) {
        setErrorMessage(
          "An error occurred while deleting the User. Please check your network connection and try again."
        );
        console.error("Error occurred during submission:", error);
      } finally {
        setIsLoading(false);
      }
    }
  }

  return (
    <ThemeProvider theme={theme}>
      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSuccessMessage("")} // Clear the success message on close
      >
        <Alert
          severity="success"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Loading Snackbar */}
      <Snackbar
        open={isLoading}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          Loading, please wait...
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setErrorMessage("")}
      >
        <Alert
          severity="error"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      <Box sx={{ padding: "40px 60px" }}>
        <Card
          variant="outlined"
          style={{
            marginTop: "40px",
            marginBottom: "40px",
            borderRadius: 0,
          }}
        >
          <CardHeader
            title={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center">
                  <EventIcon
                    sx={{
                      fontSize: "40px",
                      marginRight: "8px",
                    }}
                  />
                  <Typography variant="h6">Admin Hub</Typography>
                </Box>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<NavigateNextIcon fontSize="small" />}
                  sx={{ color: "white" }}
                >
                  <Typography sx={{ color: "#ffffff99" }}>Admin Hub</Typography>
                  <Typography
                    component={Link}
                    to="/delete-users"
                    sx={{ color: "#ffffff", textDecoration: "none" }}
                  >
                    Delete Users
                  </Typography>
                </Breadcrumbs>
              </Box>
            }
            sx={{
              backgroundColor: "#1e5d45",
              color: "white",
              padding: "16px",
            }}
          />
          <CardContent sx={{ padding: "20px" }}>
            <Card variant="outlined" sx={{ padding: "20px" }}>
              <CardHeader
                title={
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    Delete Users
                  </Typography>
                }
                sx={{ marginBottom: 2 }}
              />
              <Grid item xs={12}>
                <Grid container spacing={3} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Autocomplete
                      freeSolo
                      options={usersCache}
                      getOptionLabel={(user) => user?.name || ""}
                      value={selectedUser}
                      onChange={(event, newValue) =>
                        handleSearchUser(event, newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Name"
                          variant="outlined"
                          fullWidth
                          error={!!error}
                          helperText={error || ""}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4} container alignItems="center">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSearchButtonClick}
                      disabled={isLoading}
                    >
                      {isLoading ? "Searching..." : "Search"}{" "}
                    </Button>
                    {isFormVisible && (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={handleSubmit}
                        sx={{ ml: 2 }}
                        disabled={isLoading}
                      >
                        Delete Record
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <StyledHr />

              {isFormVisible && user && (
                <form
                  onSubmit={handleSubmit}
                  className="user-form"
                  autoComplete="off"
                  noValidate
                >
                  <Grid container spacing={3}>
                    <Grid item>
                      <Card
                        variant="outlined"
                        sx={{ padding: "20px", marginBottom: "20px" }}
                      >
                        <CardHeader
                          title={
                            <Typography
                              variant="h6"
                              sx={{
                                paddingBottom: "1rem",
                                fontWeight: "bold",
                                textTransform: "uppercase",
                                fontSize: "1rem",
                              }}
                            >
                              Basic Information
                            </Typography>
                          }
                        />
                        <CardContent>
                          <Grid container spacing={3}>
                            <Grid item xs={4}>
                              <TextField
                                label="First Name"
                                id="firstname"
                                name="firstname"
                                value={user.firstname}
                                size="small"
                                required
                                fullWidth
                                disabled
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                label="Middle Initial"
                                id="middle_initial"
                                name="middle_initial"
                                value={user.middle_initial}
                                size="small"
                                fullWidth
                                disabled
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                label="Last Name"
                                id="lastname"
                                name="lastname"
                                value={user.lastname}
                                size="small"
                                required
                                fullWidth
                                disabled
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Username"
                                id="username"
                                name="username"
                                value={user.username}
                                size="small"
                                required
                                fullWidth
                                disabled
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Email"
                                id="email"
                                name="email"
                                type="email"
                                value={user.email}
                                size="small"
                                required
                                fullWidth
                                disabled
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Password"
                                type={
                                  showPasswords.password ? "text" : "password"
                                }
                                id="password"
                                name="password"
                                value={user.password}
                                size="small"
                                required
                                fullWidth
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton edge="end" disabled>
                                        {showPasswords.password ? (
                                          <VisibilityOff />
                                        ) : (
                                          <Visibility />
                                        )}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                                disabled
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Confirm Password"
                                type={
                                  showPasswords.confirmPassword
                                    ? "text"
                                    : "password"
                                }
                                id="confirm_password"
                                name="confirm_password"
                                value={user.confirm_password}
                                size="small"
                                required
                                fullWidth
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton edge="end" disabled>
                                        {showPasswords.confirmPassword ? (
                                          <VisibilityOff />
                                        ) : (
                                          <Visibility />
                                        )}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                                disabled
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Date Hired"
                                id="date_hired"
                                name="date_hired"
                                type="date"
                                value={user.date_hired}
                                size="small"
                                fullWidth
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                disabled
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Divider
                                variant="fullWidth"
                                sx={{
                                  my: 4,
                                  borderColor: "rgba(0, 0, 0, 0.12)",
                                  borderWidth: "0.5px",
                                }}
                              />
                            </Grid>
                            <Grid container spacing={3}>
                              <Grid item xs={12}>
                                <CardHeader
                                  title={
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      width="100%"
                                    >
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          fontWeight: "bold",
                                          textTransform: "uppercase",
                                          fontSize: "1rem",
                                        }}
                                        mr={2}
                                      >
                                        Contact Information (optional)
                                      </Typography>
                                      <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        size="small"
                                        disabled
                                      >
                                        Add Another Contact Person
                                      </Button>
                                    </Box>
                                  }
                                  sx={{ padding: "16px" }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <CardContent sx={{ padding: "20px" }}>
                                  <Grid container spacing={2}>
                                    {user.contact.map((contact, index) => (
                                      <React.Fragment key={index}>
                                        <Grid item xs={2}>
                                          <FormControl fullWidth size="small">
                                            <InputLabel
                                              id={`contact-type-${index}`}
                                            >
                                              Type
                                            </InputLabel>
                                            <Select
                                              labelId={`contact-type-${index}`}
                                              value={contact.type}
                                              label="Type"
                                              size="small"
                                              disabled
                                            >
                                              <MenuItem value="mobile">
                                                Mobile
                                              </MenuItem>
                                              <MenuItem value="landline">
                                                Landline
                                              </MenuItem>
                                            </Select>
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={2}>
                                          <FormControl fullWidth size="small">
                                            <InputLabel
                                              id={`country-code-${index}`}
                                            >
                                              Country Code
                                            </InputLabel>
                                            <Select
                                              labelId={`country-code-${index}`}
                                              value={contact.country_code}
                                              label="Country Code"
                                              size="small"
                                              disabled
                                            >
                                              <MenuItem value="+63">
                                                +63
                                              </MenuItem>
                                              <MenuItem value="+1">+1</MenuItem>
                                              <MenuItem value="+44">
                                                +44
                                              </MenuItem>
                                            </Select>
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={3}>
                                          <TextField
                                            label="Contact Number"
                                            id={`contact_number_${index}`}
                                            type="text"
                                            value={contact.number}
                                            size="small"
                                            fullWidth
                                            disabled
                                          />
                                        </Grid>
                                        <Grid item xs={4.5}>
                                          <TextField
                                            label="Description"
                                            id={`contact_description_${index}`}
                                            value={contact.description}
                                            size="small"
                                            fullWidth
                                            disabled
                                          />
                                        </Grid>
                                        <Grid item xs>
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            height="100%"
                                          >
                                            <Tooltip title="Delete">
                                              <span>
                                                <IconButton
                                                  // onClick={() =>
                                                  //   removeContactField(index)
                                                  // }
                                                  disabled
                                                  sx={{
                                                    color:
                                                      isLoading ||
                                                      user.contact.length === 1
                                                        ? "rgba(0, 0, 0, 0.26)"
                                                        : "#DC3545",
                                                    cursor:
                                                      isLoading ||
                                                      user.contact.length === 1
                                                        ? "default"
                                                        : "pointer",
                                                  }}
                                                >
                                                  <DeleteIcon
                                                    sx={{
                                                      fontSize: "1.5rem",
                                                    }}
                                                  />
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                          </Box>
                                        </Grid>
                                      </React.Fragment>
                                    ))}
                                  </Grid>
                                </CardContent>
                              </Grid>
                            </Grid>
                            <Grid item xs={12}>
                              <Divider
                                variant="fullWidth"
                                sx={{
                                  my: 4,
                                  borderColor: "rgba(0, 0, 0, 0.12)",
                                  borderWidth: "0.5px",
                                }}
                              />
                            </Grid>
                            <Grid container spacing={3}>
                              <Grid item xs={12}>
                                <CardHeader
                                  title={
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: "bold",
                                        textTransform: "uppercase",
                                        fontSize: "1rem",
                                      }}
                                    >
                                      User Roles *
                                    </Typography>
                                  }
                                  sx={{ padding: "16px" }}
                                />
                                <CardContent sx={{ padding: "20px" }}>
                                  <Grid container spacing={3}>
                                    <Grid item xs={6}>
                                      <FormGroup>
                                        {[
                                          "sales",
                                          "operations_admin",
                                          "operations_coordinator",
                                          "operations_technician",
                                        ].map((role, index) => (
                                          <FormControlLabel
                                            key={index}
                                            control={
                                              <Checkbox
                                                id={role}
                                                name="roles"
                                                value={role}
                                                checked={user.roles.includes(
                                                  role
                                                )}
                                                disabled
                                              />
                                            }
                                            label={role
                                              .replace(/_/g, " ")
                                              .split(" ")
                                              .map(
                                                (word) =>
                                                  word.charAt(0).toUpperCase() +
                                                  word.slice(1)
                                              )
                                              .join(" ")}
                                          />
                                        ))}
                                      </FormGroup>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormGroup>
                                        {[
                                          "finance",
                                          "account_manager",
                                          "office_admin",
                                          "customer_service",
                                        ].map((role, index) => (
                                          <FormControlLabel
                                            key={index}
                                            control={
                                              <Checkbox
                                                id={role}
                                                name="roles"
                                                value={role}
                                                checked={user.roles.includes(
                                                  role
                                                )}
                                                disabled
                                              />
                                            }
                                            label={role
                                              .replace(/_/g, " ")
                                              .split(" ")
                                              .map(
                                                (word) =>
                                                  word.charAt(0).toUpperCase() +
                                                  word.slice(1)
                                              )
                                              .join(" ")}
                                          />
                                        ))}
                                      </FormGroup>
                                    </Grid>
                                  </Grid>
                                </CardContent>
                              </Grid>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Card>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
}

export default DeleteUsersComponent;
